import type {
  PortfolioAnalyticsPropertyDto,
  PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import {
  DeprecatedZillowRegionType,
  MarketAnalyticsWebApiHandler_getCompetitorsWithSpecialOffer,
  MarketAnalyticsWebApiHandler_getCompetitorsWithSpecialOffer_Path,
  MarketAnalyticsWebApiHandler_getNewInventory,
  MarketAnalyticsWebApiHandler_getNewInventory_Path,
  MarketAnalyticsWebApiHandler_getTopNewConstructions,
  MarketAnalyticsWebApiHandler_getTopNewConstructions_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { useQuery } from '@zg-rentals/react-query5';

import { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';

export const useCompetitorsWithSpecialOffer = (enabled: boolean) => {
  const { filteredProperties, selectedFilters, visibleRegionIds } = useMainLayout();
  let allZrnNumbersInVisibleRegion: Array<string> = [];

  if (selectedFilters.listingAlias === '') {
    const allPropertiesInVisibleRegion = filteredProperties.filter((property) =>
      visibleRegionIds.some(
        (region) =>
          region.regionId === property.propertyDetails.stateRegionId ||
          region.regionId === property.propertyDetails.metroStatRegionId ||
          region.regionId === property.propertyDetails.cityRegionId ||
          region.regionId === property.propertyDetails.zipRegionId,
      ),
    );
    allZrnNumbersInVisibleRegion = allPropertiesInVisibleRegion.map((property) => property.aggregateStats.zrnNumber);
  } else {
    const property = filteredProperties.find(
      (property) => property.propertyDetails.listingAlias === selectedFilters.listingAlias,
    );
    if (property) {
      allZrnNumbersInVisibleRegion = [property.aggregateStats.zrnNumber];
    }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: [MarketAnalyticsWebApiHandler_getCompetitorsWithSpecialOffer_Path, allZrnNumbersInVisibleRegion],
    queryFn: () => MarketAnalyticsWebApiHandler_getCompetitorsWithSpecialOffer(allZrnNumbersInVisibleRegion),
    enabled: enabled,
  });

  return {
    data,
    isLoading,
    error,
  };
};

export const regionIdTypeToZillowRegionType = (regionIdType: DisplayingRegionLevel) => {
  switch (regionIdType) {
    case DisplayingRegionLevel.STATE:
      return DeprecatedZillowRegionType.State;
    case DisplayingRegionLevel.METRO_AREA:
      return DeprecatedZillowRegionType.CoreBasedStatArea;
    case DisplayingRegionLevel.CITY:
      return DeprecatedZillowRegionType.City;
    case DisplayingRegionLevel.ZIP:
      return DeprecatedZillowRegionType.ZipCode;
    default:
      return DeprecatedZillowRegionType.State;
  }
};

type RegionsWithIds = Array<{ regionId: number; regionIdType: DisplayingRegionLevel }>;

export const useNewInventory = (enabled: boolean) => {
  const { filteredProperties, visibleRegionIds } = useMainLayout();
  let allCityRegionIds: Array<number> = [];
  let regionIds: RegionsWithIds = visibleRegionIds.reduce((acc: RegionsWithIds, curr) => {
    if (curr.regionIdType === DisplayingRegionLevel.METRO_AREA) {
      const allCityRegionIdsInMetroArea = filteredProperties
        .filter((property) => property.propertyDetails.metroStatRegionId === curr.regionId)
        .map((property) => property.propertyDetails.cityRegionId);

      allCityRegionIds.push(...allCityRegionIdsInMetroArea);

      return acc;
    } else {
      acc.push(curr);
      return acc;
    }
  }, [] as RegionsWithIds);

  allCityRegionIds = [...new Set(allCityRegionIds)];

  regionIds = regionIds.concat(
    allCityRegionIds.map((regionId) => ({ regionId, regionIdType: DisplayingRegionLevel.CITY })),
  );

  return useQuery({
    queryKey: [MarketAnalyticsWebApiHandler_getNewInventory_Path, { regionIds }],
    queryFn: async () => {
      const promises = regionIds.map(({ regionId, regionIdType }) => {
        return MarketAnalyticsWebApiHandler_getNewInventory({
          limit: 20,
          numberOfDays: 60,
          offset: 0,
          zillowRegionId: regionId,
          zillowRegionType: regionIdTypeToZillowRegionType(regionIdType),
        });
      });

      const results = await Promise.allSettled(promises);

      const filteredResults = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

      return filteredResults.map((result) => result.data ?? []).flat();
    },
    enabled: enabled,
  });
};

export const useTopNewConstructions = (enabled: boolean) => {
  const { filteredProperties, visibleRegionIds } = useMainLayout();
  let allCityRegionIds: Array<number> = [];
  let regionIds: RegionsWithIds = [];

  regionIds = visibleRegionIds.reduce((acc: RegionsWithIds, curr) => {
    if (curr.regionIdType === DisplayingRegionLevel.METRO_AREA) {
      const allCityRegionIdsInMetroArea = filteredProperties
        .filter((property) => property.propertyDetails.metroStatRegionId === curr.regionId)
        .map((property) => property.propertyDetails.cityRegionId);

      allCityRegionIds.push(...allCityRegionIdsInMetroArea);

      return acc;
    } else {
      acc.push(curr);
      return acc;
    }
  }, [] as RegionsWithIds);

  allCityRegionIds = [...new Set(allCityRegionIds)];

  regionIds = regionIds.concat(
    allCityRegionIds.map((regionId) => ({ regionId, regionIdType: DisplayingRegionLevel.CITY })),
  );

  return useQuery({
    queryKey: [MarketAnalyticsWebApiHandler_getTopNewConstructions_Path, { regionIds }],
    queryFn: async () => {
      const promises = regionIds.map(({ regionId, regionIdType }) => {
        return MarketAnalyticsWebApiHandler_getTopNewConstructions({
          zillowRegionId: regionId,
          zillowRegionType: regionIdTypeToZillowRegionType(regionIdType),
        });
      });

      const results = await Promise.allSettled(promises);

      const filteredResults = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

      return filteredResults.flat();
    },
    enabled: enabled,
  });
};

export const getAllVacancyRateMapping = (properties: Array<PortfolioAnalyticsPropertyResponseDto>) => {
  const allVacancyRateMapping = new Map<number, number>();
  const regionIdKeys: Array<keyof PortfolioAnalyticsPropertyDto> = [
    'stateRegionId',
    'metroStatRegionId',
    'cityRegionId',
    'zipRegionId',
  ];

  properties.forEach((property) => {
    for (const regionIdKey of regionIdKeys) {
      const regionId = Number(property.propertyDetails[regionIdKey]);
      if (isNaN(regionId)) continue;

      if (regionId && !allVacancyRateMapping.has(regionId)) {
        const propertiesFromRegion = properties.filter(
          (p) =>
            Number(p.propertyDetails[regionIdKey]) === regionId &&
            p.aggregateStats.advertisedUnitsAverage < p.propertyDetails.numUnits,
        );
        const vacancyRate =
          propertiesFromRegion.reduce((acc, property) => {
            const vacancyPercentage =
              (property.aggregateStats.advertisedUnitsAverage || 0) / (property.propertyDetails.numUnits || 1);
            return acc + vacancyPercentage;
          }, 0) / propertiesFromRegion.length;

        allVacancyRateMapping.set(regionId, vacancyRate);
      }
    }
  });

  return allVacancyRateMapping;
};
